/**
 * Axelar Monitor Page
 *
 * Monitor Axelar (AXL) wallets, staking delegations, rewards, and cross-chain activity.
 * Axelar is a cross-chain communication protocol using Cosmos SDK.
 * Similar design to HederaMonitor and TheGraphMonitor.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useAxelarMonitor, useAxelarSupportedChains } from '../hooks/useAxelarWallet';
import { formatAXL, AXL_DENOM, axelarApi } from '../services/axelarApi';
import { useToast } from '../components/Toast';
import { AxelarHealthMetrics } from '../components/axelar/AxelarHealthMetrics';
import { AxlSupplyBreakdown } from '../components/axelar/AxlSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import type { AxelarWalletData } from '../types/axelar.types';

// ============================================
// CONSTANTS & STYLES
// ============================================

const AXELAR_COLORS = {
  primary: '#00D1FF', // Axelar cyan
  secondary: '#7B3FE4', // Axelar purple
  accent: '#a855f7',
  gradient: 'linear-gradient(135deg, #7B3FE4, #00D1FF)',
  lightGradient: 'linear-gradient(135deg, rgba(123, 63, 228, 0.15), rgba(0, 209, 255, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'overview' | 'staking' | 'chains' | 'transactions';

const s = {
  page: {
    padding: '0',
    maxWidth: '1400px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: AXELAR_COLORS.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: (color: string) => ({
    background: `${color}10`,
    border: `1px solid ${color}30`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'monospace',
    outline: 'none',
  },
  button: {
    padding: '12px 20px',
    background: AXELAR_COLORS.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    padding: '12px 20px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'var(--text)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '24px',
  },
  tab: (isActive: boolean) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive ? AXELAR_COLORS.gradient : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(123, 63, 228, 0.3)' : 'none',
  }),
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
  },
  delegationCard: {
    background: 'rgba(123, 63, 228, 0.08)',
    border: '1px solid rgba(123, 63, 228, 0.2)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Network Stats Banner
 */
function NetworkStatsBanner({
  axlPrice,
  networkStats,
  totalWallets,
  isLoading,
}: {
  axlPrice: number;
  networkStats: { apr: string; activeValidators: number; blockHeight: string } | null;
  totalWallets: number;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        ...s.card,
        background: AXELAR_COLORS.lightGradient,
        border: '1px solid rgba(123, 63, 228, 0.25)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('axelar.network.title', 'Axelar Network')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: AXELAR_COLORS.secondary }}>
              ${axlPrice.toFixed(4)}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>AXL</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: AXELAR_COLORS.success }}>
              {networkStats ? (parseFloat(networkStats.apr) * 100).toFixed(1) : '-'}%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('axelar.network.apr', 'Staking APR')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {networkStats?.activeValidators || '-'}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('axelar.network.validators', 'Validators')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalWallets}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('axelar.network.wallets', 'Wallets')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: isLoading ? AXELAR_COLORS.warning : AXELAR_COLORS.success,
                boxShadow: `0 0 10px ${isLoading ? AXELAR_COLORS.warning : AXELAR_COLORS.success}`,
                margin: '0 auto 4px',
              }}
            />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {isLoading
                ? t('axelar.network.syncing', 'Syncing...')
                : t('axelar.network.connected', 'Connected')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Overview Cards
 */
function StatsOverview({
  totalBalance,
  totalBalanceUsd,
  stakedBalance,
  stakedBalanceUsd,
  pendingRewards,
  pendingRewardsUsd,
  unbondingBalance,
  delegationsCount,
}: {
  totalBalance: string;
  totalBalanceUsd: number;
  stakedBalance: string;
  stakedBalanceUsd: number;
  pendingRewards: string;
  pendingRewardsUsd: number;
  unbondingBalance: string;
  delegationsCount: number;
}) {
  const { t } = useTranslation();

  const stats = [
    {
      label: t('axelar.stats.total', 'Total Balance'),
      value: `${totalBalance} AXL`,
      subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: AXELAR_COLORS.secondary,
      icon: <Icons.Wallet size={16} />,
    },
    {
      label: t('axelar.stats.staked', 'Staked'),
      value: `${stakedBalance} AXL`,
      subvalue: `$${stakedBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: AXELAR_COLORS.success,
      icon: <Icons.Lock size={16} />,
    },
    {
      label: t('axelar.stats.rewards', 'Pending Rewards'),
      value: `${pendingRewards} AXL`,
      subvalue: `$${pendingRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: AXELAR_COLORS.warning,
      icon: <Icons.TrendingUp size={16} />,
    },
    {
      label: t('axelar.stats.delegations', 'Delegations'),
      value: delegationsCount.toString(),
      subvalue: parseFloat(unbondingBalance) > 0 ? `${unbondingBalance} unbonding` : 'Active',
      color: AXELAR_COLORS.primary,
      icon: <Icons.Users size={16} />,
    },
  ];

  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
            {stat.subvalue}
          </div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Wallet Card Component
 */
function WalletCard({
  wallet,
  axlPrice,
  onRemove,
}: {
  wallet: AxelarWalletData;
  axlPrice: number;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const available = wallet.account?.balances.find((b) => b.denom === AXL_DENOM)?.amount || '0';
  const staked = wallet.account?.totalStaked || '0';
  const rewards = wallet.account?.totalRewards || '0';
  const delegations = wallet.account?.delegations.length || 0;

  return (
    <div style={s.delegationCard}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: AXELAR_COLORS.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.Axelar size={20} color="white" />
          </div>
          <div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '2px',
              }}
            >
              {wallet.label || axelarApi.isValidAddress(wallet.address)
                ? `${wallet.address.slice(0, 12)}...${wallet.address.slice(-8)}`
                : wallet.address}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                style={{
                  ...s.badge,
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: AXELAR_COLORS.success,
                }}
              >
                <Icons.Axelar size={10} color={AXELAR_COLORS.secondary} />
                {delegations} {t('axelar.delegations', 'Delegations')}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href={`https://axelarscan.io/account/${wallet.address}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'rgba(123, 63, 228, 0.15)',
              color: AXELAR_COLORS.secondary,
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              textDecoration: 'none',
            }}
          >
            <Icons.ExternalLink size={12} />
          </a>
          <button
            onClick={onRemove}
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
            }}
          >
            <Icons.Trash2 size={12} />
          </button>
        </div>
      </div>

      {wallet.isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
          <Icons.Activity size={24} style={{ opacity: 0.3, animation: 'spin 2s linear infinite' }} />
        </div>
      ) : wallet.error ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>
          <Icons.AlertTriangle size={20} style={{ marginBottom: '8px' }} />
          <p style={{ fontSize: '12px' }}>{wallet.error}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
              {t('axelar.wallet.available', 'Available')}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{formatAXL(available)} AXL</div>
            <div style={{ fontSize: '11px', color: AXELAR_COLORS.success }}>
              ${((parseInt(available) / 1e6) * axlPrice).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
              {t('axelar.wallet.staked', 'Staked')}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: AXELAR_COLORS.success }}>
              {formatAXL(staked)} AXL
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
              {t('axelar.wallet.rewards', 'Rewards')}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: AXELAR_COLORS.warning }}>
              {formatAXL(rewards)} AXL
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
              {t('axelar.wallet.delegations', 'Delegations')}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>{delegations}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Empty State Component
 */
function EmptyState() {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Icons.Wallet size={28} />,
      title: t('axelar.empty.balance', 'Balance'),
      desc: t('axelar.empty.balanceDesc', 'Track AXL holdings'),
    },
    {
      icon: <Icons.TrendingUp size={28} />,
      title: t('axelar.empty.staking', 'Staking'),
      desc: t('axelar.empty.stakingDesc', 'Monitor rewards'),
    },
    {
      icon: <Icons.Link size={28} />,
      title: t('axelar.empty.chains', 'Chains'),
      desc: t('axelar.empty.chainsDesc', 'Cross-chain networks'),
    },
    {
      icon: <Icons.Activity size={28} />,
      title: t('axelar.empty.network', 'Network'),
      desc: t('axelar.empty.networkDesc', 'Global statistics'),
    },
  ];

  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Axelar size={40} color="white" />}
      title={t('axelar.empty.title', 'Welcome to Axelar Monitor')}
      description={t(
        'axelar.empty.description',
        'Track your AXL holdings, monitor staking rewards, view cross-chain activity, and analyze network stats - all without connecting your wallet.',
      )}
      colors={AXELAR_COLORS}
    >
      {/* Axelar-specific feature grid below the description. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '16px',
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        {features.map((feature, idx) => (
          <div
            key={idx}
            style={{
              padding: '20px 16px',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <div style={{ color: AXELAR_COLORS.secondary, marginBottom: '12px' }}>
              {feature.icon}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
              {feature.title}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{feature.desc}</div>
          </div>
        ))}
      </div>
    </NetworkMonitorEmptyState>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function AxelarMonitor() {
  const { t } = useTranslation();
  const toast = useToast();

  const [newWalletInput, setNewWalletInput] = useState('');
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const {
    wallets,
    isLoading,
    axlPrice,
    networkStats,
    validators,
    totals,
    addWallet,
    removeWallet,
  } = useAxelarMonitor();

  const { data: supportedChains = [] } = useAxelarSupportedChains();

  // Handle adding wallet
  const handleAddWallet = () => {
    const trimmedAddress = newWalletInput.trim();

    if (!trimmedAddress) {
      toast(t('axelar.toast.enterAddress', 'Please enter an Axelar address'), 'warning', 3000);
      return;
    }

    if (!axelarApi.isValidAddress(trimmedAddress)) {
      toast(
        t('axelar.toast.invalidFormat', 'Invalid Axelar address format. Must start with axelar1'),
        'error',
        4000
      );
      return;
    }

    if (wallets.some((w) => w.address === trimmedAddress)) {
      toast(t('axelar.toast.alreadyAdded', 'This address is already added'), 'warning', 3000);
      return;
    }

    addWallet(trimmedAddress);
    setNewWalletInput('');
    setShowAddWallet(false);
    toast(t('axelar.toast.walletAdded', 'Wallet added successfully'), 'success', 3000);
  };

  // Handle removing wallet
  const handleRemoveWallet = (address: string) => {
    if (confirm(t('axelar.confirm.removeWallet', `Remove wallet ${address.slice(0, 12)}...?`))) {
      removeWallet(address);
      toast(t('axelar.toast.walletRemoved', 'Wallet removed'), 'info', 3000);
    }
  };

  // Validate input
  const isValidInput = newWalletInput.trim() && axelarApi.isValidAddress(newWalletInput.trim());

  // Tabs configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('axelar.tabs.overview', 'Overview'), icon: <Icons.BarChart size={16} /> },
    { id: 'staking', label: t('axelar.tabs.staking', 'Staking'), icon: <Icons.Lock size={16} /> },
    { id: 'chains', label: t('axelar.tabs.chains', 'Chains'), icon: <Icons.Link size={16} /> },
    { id: 'transactions', label: t('axelar.tabs.transactions', 'Transactions'), icon: <Icons.Clock size={16} /> },
  ];

  // Aggregate all delegations
  const allDelegations = wallets.flatMap((w) =>
    (w.account?.delegations || []).map((d) => ({
      ...d,
      walletLabel: w.label || `${w.address.slice(0, 8)}...`,
    }))
  );

  // Calculate stats
  const stakedBalanceUsd = (parseFloat(totals.stakedAXL) || 0) * axlPrice;
  const pendingRewardsUsd = (parseFloat(totals.rewardsAXL) || 0) * axlPrice;
  const delegationsCount = allDelegations.length;

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={
          <div style={s.titleIcon}>
            <Icons.Axelar size={20} color="white" />
          </div>
        }
        title="Axelar Monitor"
        subtitle={t(
          'axelar.subtitle',
          'Monitor your AXL holdings, staking rewards, and cross-chain activity',
        )}
        primaryAction={
          showAddWallet
            ? {
                label: t('common.cancel', 'Cancel'),
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddWallet(false),
                variant: 'secondary',
              }
            : {
                label: t('axelar.addWallet', 'Add Wallet'),
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddWallet(true),
              }
        }
        colors={AXELAR_COLORS}
      />

      {showAddWallet && (
        <NetworkAddressInputForm
          value={newWalletInput}
          onChange={setNewWalletInput}
          onSubmit={handleAddWallet}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('axelar.placeholder', 'Enter Axelar address (axelar1...)')}
          isValid={newWalletInput.trim() ? Boolean(isValidInput) : null}
          helperText={t('axelar.hint', 'Enter your Axelar address starting with axelar1')}
          colors={AXELAR_COLORS}
        />
      )}

      {/* Network Stats Banner */}
      <NetworkStatsBanner
        axlPrice={axlPrice}
        networkStats={networkStats}
        totalWallets={wallets.length}
        isLoading={isLoading}
      />

      {/* V2.K.6 — AXL supply breakdown (mint inflation vs burn 98 %
          fees Cobalt + vesting end 2026). Fed by CoinGecko, refresh
          every 5 min. */}
      <AxlSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <AxelarHealthMetrics />

      {/* Main Content */}
      {wallets.length > 0 ? (
        <>
          {/* Stats Overview */}
          <StatsOverview
            totalBalance={totals.totalAXL}
            totalBalanceUsd={totals.totalUSD}
            stakedBalance={totals.stakedAXL}
            stakedBalanceUsd={stakedBalanceUsd}
            pendingRewards={totals.rewardsAXL}
            pendingRewardsUsd={pendingRewardsUsd}
            unbondingBalance={totals.unbondingAXL}
            delegationsCount={delegationsCount}
          />

          {/* Tabs */}
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={AXELAR_COLORS}
            ariaLabel="Axelar monitor tabs"
          />

          {/* Tab Content: Overview */}
          {activeTab === 'overview' && (
            <div>
              <h2
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Icons.Wallet size={18} />
                {t('axelar.wallets', 'Wallets')} ({wallets.length})
              </h2>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Icons.Activity
                    size={32}
                    style={{ opacity: 0.3, animation: 'spin 2s linear infinite' }}
                  />
                  <p style={{ color: 'var(--muted)', marginTop: '12px' }}>
                    {t('axelar.loading', 'Loading...')}
                  </p>
                </div>
              ) : (
                wallets.map((wallet) => (
                  <WalletCard
                    key={wallet.address}
                    wallet={wallet}
                    axlPrice={axlPrice}
                    onRemove={() => handleRemoveWallet(wallet.address)}
                  />
                ))
              )}
            </div>
          )}

          {/* Tab Content: Staking */}
          {activeTab === 'staking' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('axelar.staking.title', 'Staking Delegations')} ({allDelegations.length})
              </h3>
              {allDelegations.length > 0 ? (
                allDelegations.map((delegation, idx) => (
                  <div key={`${delegation.validatorAddress}-${idx}`} style={s.delegationCard}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: AXELAR_COLORS.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icons.Server size={18} color="white" />
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600 }}>
                            {delegation.validator?.moniker ||
                              `${delegation.validatorAddress.slice(0, 16)}...`}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                            Commission:{' '}
                            {((parseFloat(delegation.validator?.commission.rate || '0')) * 100).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '16px', fontWeight: 600 }}>
                          {formatAXL(delegation.balance.amount)} AXL
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          ${((parseInt(delegation.balance.amount) / 1e6) * axlPrice).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          ...s.badge,
                          background: 'rgba(123, 63, 228, 0.15)',
                          color: AXELAR_COLORS.secondary,
                        }}
                      >
                        {delegation.walletLabel}
                      </span>
                      <a
                        href={`https://axelarscan.io/validator/${delegation.validatorAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '11px',
                          color: AXELAR_COLORS.primary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Icons.ExternalLink size={12} />
                        View Validator
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('axelar.staking.empty', 'No active delegations found')}
                </div>
              )}

              {/* Top Validators */}
              {validators.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                    {t('axelar.validators.title', 'Top Validators')}
                  </h3>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '12px',
                    }}
                  >
                    {validators.slice(0, 10).map((validator, idx) => (
                      <div key={validator.operatorAddress} style={s.delegationCard}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                background: AXELAR_COLORS.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                                {validator.moniker}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                                {(parseFloat(validator.commission.rate) * 100).toFixed(1)}% commission
                              </div>
                            </div>
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>
                            {(parseInt(validator.tokens) / 1e6).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}{' '}
                            AXL
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab Content: Chains */}
          {activeTab === 'chains' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('axelar.chains.title', 'Supported Chains')} ({supportedChains.length})
              </h3>
              <p style={{ color: 'var(--muted)', marginBottom: '20px', fontSize: '14px' }}>
                {t(
                  'axelar.chains.description',
                  'Axelar connects blockchains for seamless cross-chain communication'
                )}
              </p>
              {supportedChains.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '12px',
                  }}
                >
                  {supportedChains.slice(0, 30).map((chain) => (
                    <div
                      key={chain.id}
                      style={{
                        ...s.delegationCard,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: 0,
                      }}
                    >
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(0, 0, 0, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {chain.image ? (
                          <img
                            src={chain.image}
                            alt={chain.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Icons.Link size={18} style={{ opacity: 0.5 }} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>{chain.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          {chain.native_token}
                        </div>
                      </div>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background:
                            chain.status === 'active'
                              ? 'rgba(16, 185, 129, 0.15)'
                              : 'rgba(239, 68, 68, 0.15)',
                          color: chain.status === 'active' ? AXELAR_COLORS.success : '#ef4444',
                        }}
                      >
                        {chain.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('axelar.chains.loading', 'Loading chains...')}
                </div>
              )}
            </div>
          )}

          {/* Tab Content: Transactions */}
          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('axelar.transactions.title', 'Transaction History')}
              </h3>
              <div
                style={{
                  ...s.card,
                  textAlign: 'center',
                  padding: '60px 20px',
                }}
              >
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'rgba(123, 63, 228, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Icons.Clock size={28} style={{ opacity: 0.5, color: AXELAR_COLORS.secondary }} />
                </div>
                <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                  {t('axelar.transactions.coming', 'Coming Soon')}
                </p>
                <p style={{ color: 'var(--muted)', marginBottom: '20px', fontSize: '14px' }}>
                  {t('axelar.transactions.useExplorer', 'Use Axelarscan to view your transactions')}
                </p>
                <a
                  href="https://axelarscan.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    background: 'rgba(123, 63, 228, 0.15)',
                    color: AXELAR_COLORS.secondary,
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                  }}
                >
                  <Icons.ExternalLink size={16} />
                  Open Axelarscan
                </a>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState />
      )}

      {/* Info Footer */}
      <div
        style={{
          marginTop: '30px',
          padding: '16px 20px',
          background: 'rgba(123, 63, 228, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(123, 63, 228, 0.15)',
          fontSize: '12px',
          color: 'var(--muted)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Icons.Info size={14} style={{ color: AXELAR_COLORS.secondary }} />
          <strong style={{ color: 'var(--text)' }}>
            {t('axelar.info.title', 'About Axelar Monitor')}
          </strong>
        </div>
        <p style={{ lineHeight: 1.6 }}>
          {t(
            'axelar.info.description',
            'This page uses the Axelar LCD public API to fetch account data. No wallet connection is required - just enter your Axelar address to start monitoring. Data is refreshed every minute automatically.'
          )}
        </p>
      </div>
    </div>
  );
}
